import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { useConfirm } from '../components/feedback.jsx';
import { IconPlus } from '../components/icons.jsx';
import { t } from '../i18n/index.jsx';

const AUTONOMY_LABEL = {
  read_only: 'read-only', approval: 'approval required',
  non_destructive: 'auto (non-destructive)', full: 'full access',
};

const SUGGESTIONS = [
  'Which payment milestones are due in the next 30 days?',
  'List fund clients still in "preparing" status.',
  'Which contracts expire in the next 30 days?',
  'Summarise active projects and their commissions.',
];

export default function Assistant() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [status, setStatus] = useState(null);
  const [convos, setConvos] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [pending, setPending] = useState([]);
  const [restorePoints, setRestorePoints] = useState([]);
  const [showRestore, setShowRestore] = useState(false);
  const [images, setImages] = useState([]);       // data URLs attached to the next message
  const [files, setFiles] = useState([]);         // [{name,text,empty}] extracted PDFs/docs
  const [dragOver, setDragOver] = useState(false);
  const [recording, setRecording] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  const [progress, setProgress] = useState('');
  const scrollRef = useRef(null);
  const imgInputRef = useRef(null);
  const recorderRef = useRef(null);
  const audioRef = useRef(null);     // currently-playing TTS clip
  const cancelRef = useRef(false);   // user requested the AI loop to stop

  useEffect(() => { api.get('/ai/status').then(setStatus).catch(() => setStatus({ ready: false })); }, []);
  useEffect(() => { loadConvos(); loadRestore(); }, []);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, busy]);

  // Stop any audio / speech synthesis if the user navigates away mid-playback.
  useEffect(() => () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
  }, []);

  async function loadRestore() {
    try { setRestorePoints((await api.get('/ai/restore-points')).data); } catch { /* ignore */ }
  }
  async function loadPending(convId) {
    if (!convId) { setPending([]); return; }
    try { setPending((await api.get(`/ai/actions?conversation_id=${convId}`)).data); } catch { setPending([]); }
  }

  async function loadConvos() {
    try {
      const r = await api.get('/ai/conversations');
      setConvos(r.data);
      if (r.data.length && activeId == null) openConvo(r.data[0].id);
    } catch { /* ignore */ }
  }

  async function openConvo(id) {
    setActiveId(id); setErr('');
    try {
      const r = await api.get(`/ai/conversations/${id}`);
      setMessages(r.messages || []);
      loadPending(id);
    } catch (e) { setErr(e.message); }
  }

  function newChat() {
    setActiveId(null); setMessages([]); setErr(''); setInput(''); setPending([]);
  }

  async function renameConvo(c) {
    const title = prompt(t('Rename conversation'), c.title || '');
    if (title == null) return;
    await api.patch(`/ai/conversations/${c.id}`, { title });
    loadConvos();
  }

  async function deleteConvo(c) {
    if (!(await confirm({ title: t('Delete conversation'), message: t('Delete this conversation and its history?'), confirmLabel: t('Delete'), danger: true }))) return;
    await api.del(`/ai/conversations/${c.id}`);
    if (activeId === c.id) newChat();
    loadConvos();
  }

  async function send(text) {
    const content = (text ?? input).trim();
    const imgs = images;
    const atts = (files || []).filter((f) => f && f.name);
    if ((!content && imgs.length === 0 && atts.length === 0) || busy) return;
    setErr('');
    setMessages((m) => [...m, { role: 'user', content: content || '(attachment)', images: imgs, files: atts.map((f) => f.name) }]);
    setInput(''); setImages([]); setFiles([]);
    setBusy(true);
    try {
      let res = await api.post('/ai/chat', {
        conversation_id: activeId, message: content, images: imgs,
        attachments: atts.map((f) => ({ name: f.name, text: f.text || '' })),
      });
      const convId = res.conversation_id;
      if (!activeId) setActiveId(convId);
      // Long jobs return in pieces: keep resuming until the turn is done.
      cancelRef.current = false;
      let guard = 0;
      while (res.continue && guard++ < 40 && !cancelRef.current) {
        setProgress(t('Working… {n} step(s)', { n: res.steps || 0 }));
        res = await api.post('/ai/chat', { conversation_id: convId, continue: true });
      }
      setProgress('');
      if (cancelRef.current) {
        setErr(t('Stopped. The assistant may not have finished its task.'));
      } else if (res.continue) {
        setErr(t('The assistant stopped after 40 steps without finishing. Ask it to continue if needed.'));
      }
      setMessages((m) => [...m, { role: 'assistant', content: res.message?.content ?? '(no response)', tools_used: res.message?.tools_used }]);
      setPending(res.pending_actions || []);
      loadConvos();
      if (res.restore_point) loadRestore();
    } catch (e) {
      setErr(e.message || t('The assistant could not respond.'));
    } finally { setBusy(false); setProgress(''); }
  }

  // Unified handler for the 📎 picker, drag-drop and paste. Images → vision;
  // PDFs/docs → server-side text extraction.
  async function addFiles(fileList) {
    const arr = Array.from(fileList || []);
    for (const f of arr) {
      if (f.type && f.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => setImages((xs) => [...xs, reader.result].slice(0, 4));
        reader.readAsDataURL(f);
      } else {
        try {
          const fd = new FormData();
          fd.append('file', f);
          const r = await api.upload('/ai/extract', fd);
          if (!r || typeof r.name !== 'string') {
            // Server returned 200 but wrong shape (should be rare now).
            // Try to surface a better message if the body had an error field.
            const msg = (r && (r.error || r.detail)) || 'extract failed';
            throw new Error(msg);
          }
          setFiles((xs) => [...xs, { name: r.name, text: r.text || '', empty: !!r.empty }].slice(0, 10));
        } catch (e) { setErr(`${(f && f.name) || 'file'}: ${e.message || e}`); }
      }
    }
  }
  function onPickFiles(e) { addFiles(e.target.files); if (imgInputRef.current) imgInputRef.current.value = ''; }
  function onDrop(e) { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer?.files); }
  function onPaste(e) { const f = e.clipboardData?.files; if (f && f.length) { e.preventDefault(); addFiles(f); } }

  async function toggleMic() {
    if (recording) { recorderRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (ev) => chunks.push(ev.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: 'audio/webm' });
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'voice.webm');
          const r = await api.upload('/ai/transcribe', fd);
          setInput((v) => (v ? v + ' ' : '') + (r.text || ''));
        } catch (e) { setErr(e.message); }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch { setErr(t('Microphone not available or permission denied.')); }
  }

  async function speak(idx, text) {
    setSpeakingId(idx);
    try {
      if (audioRef.current) audioRef.current.pause();
      const r = await api.post('/ai/speak', { text });
      const audio = new Audio(r.audio);
      audioRef.current = audio;
      audio.onended = () => { setSpeakingId(null); audioRef.current = null; };
      audio.play();
    } catch (e) { setErr(e.message); setSpeakingId(null); }
  }

  async function approve(id) {
    await api.post(`/ai/actions/${id}/approve`);
    await loadPending(activeId); loadRestore();
  }
  async function reject(id) {
    await api.post(`/ai/actions/${id}/reject`);
    loadPending(activeId);
  }
  async function approveAll() {
    if (!activeId) return;
    await api.post('/ai/actions/approve-all', { conversation_id: activeId });
    await loadPending(activeId); loadRestore();
  }
  async function undoRestore(rp) {
    if (!(await confirm({ title: t('Undo AI changes'), message: t('Undo "{label}"? This reverts {n} change(s).', { label: rp.label, n: rp.action_count }), confirmLabel: t('Undo'), danger: true }))) return;
    await api.post(`/ai/restore-points/${rp.id}/undo`);
    loadRestore();
  }

  const hasCompacted = messages.some((m) => m.compacted);
  let dividerShown = false;

  return (
    <div className="page fade-in assistant-page">
      <div className="page-head">
        <h1 className="page-title">{t('Assistant')}</h1>
        {status && (
          <span className="muted sm">
            {status.ready ? t('Powered by {provider}', { provider: status.provider }) : t('Not configured')}
            {status.autonomy && <> · <span title={t('AI autonomy level')}>{t(AUTONOMY_LABEL[status.autonomy] || status.autonomy)}</span></>}
          </span>
        )}
      </div>

      {status && !status.ready && (
        <div className="alert error">
          {t('The AI assistant isn’t configured yet.')}{' '}
          {user.role === 'admin'
            ? <>{t('Set a provider, model and API key in')} <Link to="/settings">{t('Settings')}</Link>.</>
            : t('Ask an admin to set it up in Settings.')}
        </div>
      )}

      <div className="assistant-layout">
        <aside className="chat-rail">
          <button className="btn ghost sm chat-new" onClick={newChat}>
            <IconPlus width={13} height={13} /> {t('New chat')}
          </button>
          <div className="convo-list">
            {convos.length === 0 && <div className="muted sm" style={{ padding: '8px 10px' }}>{t('No conversations yet.')}</div>}
            {convos.map((c) => (
              <div key={c.id} className={`convo-item ${activeId === c.id ? 'active' : ''}`} onClick={() => openConvo(c.id)}>
                <span className="convo-title">{c.title || t('Untitled')}</span>
                <span className="convo-actions">
                  <button className="icon-btn xs" title={t('Rename')} onClick={(e) => { e.stopPropagation(); renameConvo(c); }}>✎</button>
                  <button className="icon-btn xs" title={t('Delete')} onClick={(e) => { e.stopPropagation(); deleteConvo(c); }}>🗑</button>
                </span>
              </div>
            ))}
          </div>
          {restorePoints.length > 0 && (
            <div className="restore-box">
              <button className="btn ghost sm" onClick={() => setShowRestore((s) => !s)}>
                {t('Restore points')} ({restorePoints.length})
              </button>
              {showRestore && (
                <div className="restore-list">
                  {restorePoints.map((rp) => (
                    <div key={rp.id} className={`restore-item ${rp.undone_at ? 'undone' : ''}`}>
                      <span className="restore-label" title={rp.label}>{rp.label}</span>
                      {rp.undone_at
                        ? <span className="muted sm">{t('undone')}</span>
                        : <button className="icon-btn xs" title={t('Undo')} onClick={() => undoRestore(rp)}>↺</button>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </aside>

        <div className={`chat-main ${dragOver ? 'dragover' : ''}`}
          onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
          onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
          onDrop={onDrop}>
          {dragOver && <div className="drop-hint">{t('Drop images, PDFs or documents to attach')}</div>}
          <div className="chat" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="chat-empty">
                <p className="muted">{t('Ask about funds, payment milestones, fund clients, projects, contracts, tasks, exclusivities or commissions.')}</p>
                <div className="suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} className="chip" onClick={() => send(t(s))} disabled={!status?.ready}>{t(s)}</button>
                  ))}
                </div>
              </div>
            )}
            {hasCompacted && <div className="compaction-note">{t('Older messages were summarized to keep the chat fast.')}</div>}
            {messages.map((m, i) => {
              const showDivider = hasCompacted && !m.compacted && !dividerShown && i > 0;
              if (showDivider) dividerShown = true;
              return (
                <div key={i}>
                  {showDivider && <div className="compaction-divider"><span>recent messages</span></div>}
                  <div className={`bubble-row ${m.role}`}>
                    <div className={`bubble ${m.role}`}>
                      {m.images?.length > 0 && (
                        <div className="bubble-images">
                          {m.images.map((src, k) => <img key={k} src={src} alt="attachment" />)}
                        </div>
                      )}
                      {m.files?.length > 0 && (
                        <div className="bubble-files">{m.files.map((n, k) => <span key={k} className="bubble-file">📄 {n}</span>)}</div>
                      )}
                      {m.content || <span className="muted">…</span>}
                      {m.tools_used?.length > 0 && (
                        <div className="tool-note">{t('used')}: {m.tools_used.map((u) => u.tool).join(', ')}</div>
                      )}
                      {m.role === 'assistant' && m.content && status?.tts && (
                        <button className="speak-btn" title={t('Play')} disabled={speakingId === i}
                          onClick={() => speak(i, m.content)}>{speakingId === i ? t('▶ playing…') : '🔊'}</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {busy && (
              <div className="bubble-row assistant" style={{ alignItems: 'center', gap: 8 }}>
                <div className="bubble assistant typing">{progress || t('Thinking…')}</div>
                <button className="btn ghost sm" onClick={() => { cancelRef.current = true; }}>{t('Cancel')}</button>
              </div>
            )}
          </div>

          {pending.length > 0 && (
            <div className="pending-box">
              <div className="pending-head">
                <strong>{pending.length > 1 ? t('{n} actions awaiting your approval', { n: pending.length }) : t('1 action awaiting your approval')}</strong>
                <button className="btn primary sm" onClick={approveAll}>{t('Approve all')}</button>
              </div>
              <ul className="pending-list">
                {pending.map((a) => (
                  <li key={a.id}>
                    <span className="pending-summary">{a.summary || a.tool}{a.kind === 'destructive' && <span className="badge red">{t('deletes data')}</span>}</span>
                    <span className="pending-actions">
                      <button className="btn ghost sm" onClick={() => approve(a.id)}>{t('Approve')}</button>
                      <button className="btn ghost sm" onClick={() => reject(a.id)}>{t('Reject')}</button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {err && <div className="alert error">{err}</div>}

          {(images.length > 0 || files.length > 0) && (
            <div className="attach-previews">
              {images.map((src, k) => (
                <div key={`i${k}`} className="attach-thumb">
                  <img src={src} alt="attachment" />
                  <button className="attach-remove" onClick={() => setImages((xs) => xs.filter((_, j) => j !== k))}>×</button>
                </div>
              ))}
              {files.map((f, k) => f && f.name ? (
                <div key={`f${k}`} className="attach-file" title={f.empty ? t('No text could be extracted (maybe scanned)') : f.name}>
                  📄 <span className="attach-file-name">{f.name}</span>{f.empty && <span className="badge amber">{t('no text')}</span>}
                  <button className="attach-remove inline" onClick={() => setFiles((xs) => xs.filter((_, j) => j !== k))}>×</button>
                </div>
              ) : null)}
            </div>
          )}

          <form className="chat-input" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <input ref={imgInputRef} type="file" accept="image/*,.pdf,.docx,.txt,.csv,.md,.json" multiple hidden onChange={onPickFiles} />
            <button type="button" className="icon-btn" title={t('Attach images or PDFs/docs')} disabled={!status?.ready || busy}
              onClick={() => imgInputRef.current?.click()}>📎</button>
            {status?.stt && (
              <button type="button" className={`icon-btn ${recording ? 'recording' : ''}`} title={recording ? t('Stop recording') : t('Record voice')}
                disabled={!status?.ready || busy} onClick={toggleMic}>{recording ? '⏺' : '🎤'}</button>
            )}
            <textarea
              rows={1}
              placeholder={recording ? t('Listening… tap stop when done') : t('Ask the assistant…')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              onPaste={onPaste}
              disabled={!status?.ready || busy}
            />
            <button className="btn primary" disabled={!status?.ready || busy || (!input.trim() && images.length === 0)}>{t('Send')}</button>
          </form>
        </div>
      </div>
    </div>
  );
}
